import { Grid, Text, Heading, Message, Divider, Checkbox, useToast, Skeleton, Link } from '@afterpaytouch/core'
import { Trans, useTranslation } from 'next-i18next'
import React, { ChangeEvent, FunctionComponent, useCallback, useEffect, useState } from 'react'
import { Head } from '../..'
import { useAmplitudeWithEnduringEventProperties } from '../../../integrations/amplitude'
import { TrackingEvent } from '../../../model/amplitude'
import {
  convertSubscriptionStatusToBoolean,
  useGetMarketingPreferencesQuery,
  useGetPersonalizedAdsStatusQuery,
  useSubscribeToMarketingEmailsMutation,
  useUnsubscribeFromMarketingEmailsMutation,
  useUpdatePersonalizedAdsStatusMutation,
  useGetConsentStatusQuery,
  useUpdateConsentStatusMutation,
  useConsumerCountry,
  convertPersonalizedAdsStatusToBoolean,
} from '../../../state'
import { useConsumerSizes } from '../../../utils/responsive'
import { useFlag } from '../../../hooks'
import { CreditCheckConsentRequest, Country } from '@afterpaytouch/portal-api'

const I18N_NAMESPACE = ['common', 'account']

export const PreferencesTab: FunctionComponent = () => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const { subheadingSize } = useConsumerSizes()
  const setToast = useToast()

  const {
    data: marketingPreferences,
    isLoading: isMarketingPreferencesLoading,
    isError: isMarketingPreferencesError,
    refetch: refetchGetMarketingPreferencesQuery,
  } = useGetMarketingPreferencesQuery()

  const [subscribeToMarketingEmails, { isLoading: isSubscribeToMarketingEmailsLoading }] = useSubscribeToMarketingEmailsMutation()
  const [unsubscribeFromMarketingEmails, { isLoading: isUnsubscribeFromMarketingEmailsLoading }] = useUnsubscribeFromMarketingEmailsMutation()

  // @ts-ignore: OPERATION BLEED STOPPER
  const defaultChecked = convertSubscriptionStatusToBoolean(marketingPreferences?.status) && convertSubscriptionStatusToBoolean(marketingPreferences?.email)

  const [marketingEmailsLabel, setMarketingEmailsLabel] = useState<string>('')

  const LABEL = {
    ON: t('common:on'),
    OFF: t('common:off'),
  }

  useEffect(() => {
    setMarketingEmailsLabel(defaultChecked ? LABEL.ON : LABEL.OFF)
  }, [LABEL.OFF, LABEL.ON, defaultChecked])

  const updateMarketingEmailsPreference = async (e: ChangeEvent<HTMLInputElement>, revertOptimisticUpdate): Promise<void> => {
    const { checked } = e.target
    const request = checked ? subscribeToMarketingEmails : unsubscribeFromMarketingEmails
    setMarketingEmailsLabel(checked ? LABEL.ON : LABEL.OFF)
    try {
      await request({}).unwrap()
      setToast({ message: t('account:tab:preferences:toast:success'), kind: 'Success', manualDismiss: true, testNameSpace: 'marketing-success' })
      logEvent(TrackingEvent.TOGGLE_MARKETING_EMAILS, {
        toggle: checked,
        success: true,
      })
    } catch {
      logEvent(TrackingEvent.TOGGLE_MARKETING_EMAILS, {
        toggle: checked,
        success: false,
      })
      setMarketingEmailsLabel(!checked ? LABEL.ON : LABEL.OFF)
      revertOptimisticUpdate()
      setToast({
        message: t('account:tab:preferences:toast:error'),
        kind: 'Failure',
        manualDismiss: true,
      })
    }
  }

  const refetchPreferences = useCallback((): void => {
    if (isMarketingPreferencesError) {
      refetchGetMarketingPreferencesQuery()
    }
  }, [isMarketingPreferencesError, refetchGetMarketingPreferencesQuery])

  const PERSONALIZED_ADS_SUPPORTED_COUNTRY_CODES = [Country.AU, Country.US, Country.GB, Country.NZ]

  const consumerCountry = useConsumerCountry()
  const isUSConsumer = consumerCountry === 'US'
  const isPersonalizedAdsSupportedCountry = typeof consumerCountry !== 'undefined' && PERSONALIZED_ADS_SUPPORTED_COUNTRY_CODES.includes(consumerCountry)

  const prescreenedOffersFeatureEnabled = useFlag('consumer-portal-prescreened-offers-opt-out-enabled')
  const personalizedAdsFeatureEnabled = useFlag('consumer-portal-personalized-ads-enabled')
  const showPersonalizedAdsOpt = isPersonalizedAdsSupportedCountry && personalizedAdsFeatureEnabled

  const {
    data: prescreenedOffersData,
    isLoading: isPrescreenedOffersLoading,
    isError: isPrescreenedOffersError,
    refetch: refetchGetPrescreenedOffersQuery,
  } = useGetConsentStatusQuery(undefined, {
    skip: !isUSConsumer,
  })

  const {
    data: personalizedAdsData,
    isLoading: isPersonalizedAdsLoading,
    isError: isPersonalizedAdsError,
    refetch: refetchPersonalizedAdsQuery,
  } = useGetPersonalizedAdsStatusQuery(undefined, {
    // Do not bother issuing the GET call unless we want to present the information in the UI.
    skip: !showPersonalizedAdsOpt,
  })

  const [updatePersonalizedAdsStatus, { isLoading: isUpdatePersonalizedAdsLoading }] = useUpdatePersonalizedAdsStatusMutation()
  const [personalizedAdsLabel, setPersonalizedAdsLabel] = useState<string>('')

  const personalizedAdsOptedIn = convertPersonalizedAdsStatusToBoolean(consumerCountry, personalizedAdsData?.personalizedAds ?? 'UNDECIDED')

  useEffect(() => {
    setPersonalizedAdsLabel(personalizedAdsOptedIn ? LABEL.ON : LABEL.OFF)
  }, [LABEL.OFF, LABEL.ON, personalizedAdsOptedIn])

  // Handle the toggle logic for personalized ads opt-out preferences. When consumers toggle to opt
  // in or out, we trigger a call through the portal-apis to the ads-service to register their
  // preference.
  const updatePersonalizedAdsPreference = async (e: ChangeEvent<HTMLInputElement>, revertOptimisticUpdate): Promise<void> => {
    const { checked } = e.target
    setPersonalizedAdsLabel(checked ? LABEL.ON : LABEL.OFF)
    try {
      await updatePersonalizedAdsStatus(checked ? 'SUBSCRIBED' : 'UNSUBSCRIBED').unwrap()
      setToast({
        message: t('account:tab:preferences:toast:success'),
        kind: 'Success',
        manualDismiss: true,
        testNameSpace: 'personalized-ads-success',
      })
      logEvent(TrackingEvent.TOGGLE_PERSONALIZED_ADS, {
        toggle: checked,
        success: true,
      })
    } catch {
      logEvent(TrackingEvent.TOGGLE_PERSONALIZED_ADS, {
        toggle: checked,
        success: false,
      })
      setPersonalizedAdsLabel(!checked ? LABEL.ON : LABEL.OFF)
      revertOptimisticUpdate()
      setToast({
        message: t('account:tab:preferences:toast:error'),
        kind: 'Failure',
        manualDismiss: true,
        testNameSpace: 'personalized-ads-error',
      })
    }
  }

  const refetchPersonalizedAds = useCallback((): void => {
    if (isPersonalizedAdsError) {
      refetchPersonalizedAdsQuery()
    }
  }, [isPersonalizedAdsError, refetchPersonalizedAdsQuery])

  const showPrescreenedOffersOpt = isUSConsumer && prescreenedOffersFeatureEnabled && prescreenedOffersData?.creditChecks?.consented === true

  const [updateConsentStatus, { isLoading: isUpdatePrescreenedOffersLoading }] = useUpdateConsentStatusMutation()

  const [prescreenedOffersLabel, setPrescreenedOffersLabel] = useState<string>('')

  const prescreenedOffersOptedIn = prescreenedOffersData?.prescreenedOffers?.consented !== false

  useEffect(() => {
    setPrescreenedOffersLabel(prescreenedOffersOptedIn ? LABEL.ON : LABEL.OFF)
  }, [LABEL.OFF, LABEL.ON, prescreenedOffersOptedIn])

  const updatePrescreenedOffersOpt = async (e: ChangeEvent<HTMLInputElement>, revertOptimisticUpdate): Promise<void> => {
    const { checked } = e.target
    setPrescreenedOffersLabel(checked ? LABEL.ON : LABEL.OFF)
    const request: CreditCheckConsentRequest = {
      consentStatus: {
        prescreenedOffers: {
          consented: checked,
          version: 'V1',
        },
      },
      channel: 'PORTAL_PREFERENCES',
    }
    try {
      await updateConsentStatus(request).unwrap()
      setToast({ message: t('account:tab:preferences:toast:prescreenedOffersSuccess'), kind: 'Success', manualDismiss: true })
    } catch {
      setPrescreenedOffersLabel(!checked ? LABEL.ON : LABEL.OFF)
      revertOptimisticUpdate()
      setToast({
        message: t('account:tab:preferences:toast:error'),
        kind: 'Failure',
        manualDismiss: true,
      })
    }
  }

  const refetchPrescreenedOffers = useCallback((): void => {
    if (isPrescreenedOffersError) {
      refetchGetPrescreenedOffersQuery()
    }
  }, [isPrescreenedOffersError, refetchGetPrescreenedOffersQuery])

  const pageTitle = `${t('account:tab:preferences:title')} - ${t('account:title')} | ${t('common:brandName')}`

  return (
    <>
      <Head title={pageTitle} />
      <Heading bold size={subheadingSize}>
        {t('account:tab:preferences:body:title')}
      </Heading>
      <div className='mt-8 flex flex-row justify-between'>
        <Grid>
          <Grid.Item sm={12} lg={6}>
            <div className='mb-8'>
              <Text>{t('account:tab:preferences:body:description:1')}</Text>
            </div>
            <Divider kind='Hairline' />
            {isMarketingPreferencesError ? (
              <div className='mt-6 sm:mb-8 lg:mb-24'>
                <Text size='S' color='Fire'>
                  {t('account:tab:preferences:body:options:error')}{' '}
                  <span className='cursor-pointer' onClick={refetchPreferences}>
                    <Text size='S' color='Fire' underline renderAs='span' bold>
                      {t('account:tab:preferences:body:options:tryAgain')}
                    </Text>
                  </span>
                </Text>
              </div>
            ) : isMarketingPreferencesLoading ? (
              <div className='relative mt-6 h-7 w-full sm:mb-8 lg:mb-24'>
                <Skeleton />
              </div>
            ) : (
              <div className='mt-6 flex items-center justify-between sm:mb-8 lg:mb-24'>
                <Text id={t('account:tab:preferences:body:options:marketingEmails')}>{t('account:tab:preferences:body:options:marketingEmails')}</Text>
                <Checkbox
                  id='marketing-emails-checkbox'
                  reversed
                  label={marketingEmailsLabel}
                  defaultChecked={defaultChecked}
                  onChange={updateMarketingEmailsPreference}
                  aria-labelledby={t('account:tab:preferences:body:options:marketingEmails')}
                  disabled={isMarketingPreferencesLoading || isSubscribeToMarketingEmailsLoading || isUnsubscribeFromMarketingEmailsLoading}
                  kind='Toggle'
                  testNameSpace='marketing-emails'
                />
              </div>
            )}
            {showPrescreenedOffersOpt && (
              <>
                <Divider kind='Hairline' />
                <div className='mb-8'>
                  <Text>{t('account:tab:preferences:body:description:prescreenedOffers')}</Text>
                </div>
                {isPrescreenedOffersError ? (
                  <div className='mt-6 sm:mb-8 lg:mb-24'>
                    <Text size='S' color='Fire'>
                      {t('account:tab:preferences:body:options:error')}{' '}
                      <span className='cursor-pointer' onClick={refetchPrescreenedOffers}>
                        <Text size='S' color='Fire' underline renderAs='span' bold>
                          {t('account:tab:preferences:body:options:tryAgain')}
                        </Text>
                      </span>
                    </Text>
                  </div>
                ) : isPrescreenedOffersLoading ? (
                  <div className='relative mt-6 h-7 w-full sm:mb-8 lg:mb-24'>
                    <Skeleton />
                  </div>
                ) : (
                  <div className='mt-6 flex items-center justify-between sm:mb-8 lg:mb-24'>
                    <Text id={t('account:tab:preferences:body:options:prescreenedOffers')}>{t('account:tab:preferences:body:options:prescreenedOffers')}</Text>
                    <Checkbox
                      id='prescreened-offers-checkbox'
                      reversed
                      label={prescreenedOffersLabel}
                      defaultChecked={prescreenedOffersOptedIn}
                      onChange={updatePrescreenedOffersOpt}
                      aria-labelledby={t('account:tab:preferences:body:options:prescreenedOffers')}
                      disabled={isPrescreenedOffersLoading || isUpdatePrescreenedOffersLoading}
                      kind='Toggle'
                    />
                  </div>
                )}
              </>
            )}
            {showPersonalizedAdsOpt && (
              <>
                <Divider kind='Hairline' />
                {isPersonalizedAdsError ? (
                  <div className='mt-6 sm:mb-8 lg:mb-24'>
                    <Text size='S' color='Fire'>
                      {t('account:tab:preferences:body:options:error')}{' '}
                      <span className='cursor-pointer' onClick={refetchPersonalizedAds}>
                        <Text size='S' color='Fire' underline renderAs='span' bold>
                          {t('account:tab:preferences:body:options:tryAgain')}
                        </Text>
                      </span>
                    </Text>
                  </div>
                ) : isPersonalizedAdsLoading ? (
                  <div className='relative mt-6 h-7 w-full sm:mb-8 lg:mb-24' data-testid='personalized-ads-loading'>
                    <Skeleton />
                  </div>
                ) : (
                  <div className='mt-6 flex items-center justify-between sm:mb-4 lg:mb-6'>
                    <Text id={t('account:tab:preferences:body:options:personalizedAds')}>{t('account:tab:preferences:body:options:personalizedAds')}</Text>
                    <Checkbox
                      id='personalized-ads-checkbox'
                      reversed
                      label={personalizedAdsLabel}
                      defaultChecked={personalizedAdsOptedIn}
                      onChange={updatePersonalizedAdsPreference}
                      aria-labelledby={t('account:tab:preferences:body:options:personalizedAds')}
                      disabled={isPersonalizedAdsLoading || isUpdatePersonalizedAdsLoading}
                      kind='Toggle'
                      testNameSpace='personalized-ads'
                    />
                  </div>
                )}
                <div className='mb-8'>
                  <Text size='S'>
                    <Trans
                      components={{
                        // Only renders a link if the tag `learnMoreLink` is present, which is only
                        // true for the en-US locale.
                        learnMoreLink: (
                          <Link href='https://help.afterpay.com/hc/en-us/articles/900007280323-Marketing-Your-Choices' kind='Underline' target='_blank' />
                        ),
                      }}
                      i18nKey='account:tab:preferences:body:description:personalizedAds'
                    />
                  </Text>
                </div>
              </>
            )}
          </Grid.Item>
          <Grid.Item sm={12} lg={6}>
            <div className='lg:flex lg:justify-center lg:px-8'>
              <div className='max-w-[368px]'>
                <Message
                  kind='Success'
                  iconName='Info'
                  size='Regular'
                  heading={t('account:tab:preferences:body:message:title')}
                  description={t('account:tab:preferences:body:message:description')}
                />
              </div>
            </div>
          </Grid.Item>
        </Grid>
      </div>
    </>
  )
}
